import { useState, useEffect, useRef } from 'react'
import { Link, router } from '@inertiajs/react'
import { AdminLayout } from '@/layouts/AdminLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Button, buttonVariants } from '@/components/ui/button'
import { Plus, Search, X } from 'lucide-react'

interface Organization {
  id: number
  name: string
  slug: string
  owner_name: string | null
  member_count: number
  subscription_status: string
  subscription_plan: string
  onboarding_progress: string
  onboarding_stuck_at: string | null
  created_at: string
}

interface Props {
  organizations: Organization[]
  meta: { current_page: number; total_pages: number; total_count: number }
  filters: { search?: string }
}

const STEP_LABELS: Record<string, string> = {
  services: 'Servicios',
  demo_conversation: 'Prueba del agente',
  whatsapp: 'WhatsApp',
}

function planBadge(plan: string, status: string) {
  const planMap: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' | 'default' }> = {
    pro: { label: 'Pro', variant: 'default' },
    basic: { label: 'Basic', variant: 'secondary' },
    free: { label: 'Free', variant: 'secondary' },
  }
  const statusMap: Record<string, { label: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
    active: { label: 'Activo', variant: 'success' },
    trialing: { label: 'Trial', variant: 'warning' },
    canceled: { label: 'Cancelado', variant: 'destructive' },
    past_due: { label: 'Vencido', variant: 'destructive' },
  }
  const planInfo = planMap[plan] || { label: plan, variant: 'secondary' as const }
  const statusInfo = statusMap[status]
  return (
    <div className="flex items-center gap-1.5">
      <Badge variant={planInfo.variant}>{planInfo.label}</Badge>
      {statusInfo && status !== 'active' && (
        <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
      )}
    </div>
  )
}

export default function Index({ organizations, meta, filters }: Props) {
  const [search, setSearch] = useState(filters.search || '')
  const didMount = useRef(false)

  useEffect(() => {
    if (!didMount.current) { didMount.current = true; return }
    const id = setTimeout(() => {
      const params: Record<string, string> = {}
      if (search) params.search = search
      params.page = '1'
      router.get('/admin/organizations', params, { preserveState: true, replace: true })
    }, 400)
    return () => clearTimeout(id)
  }, [search])

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-foreground">Organizaciones</h1>
          <div className="flex w-full gap-2 sm:w-auto">
            <Link href="/admin/organizations/new" className={buttonVariants()}><Plus className="mr-2 h-4 w-4" /> Nueva organización</Link>
            <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Buscar por nombre o slug..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-10"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
            </div>
          </div>
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organización</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Miembros</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Onboarding</TableHead>
                  <TableHead>Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {organizations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                      No hay organizaciones.
                    </TableCell>
                  </TableRow>
                ) : (
                  organizations.map((org) => (
                    <TableRow key={org.id}>
                      <TableCell>
                        <Link href={`/admin/organizations/${org.id}`} className="font-medium hover:underline">
                          {org.name}
                        </Link>
                        <p className="text-xs text-muted-foreground">{org.slug}</p>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{org.owner_name || '—'}</TableCell>
                      <TableCell>{org.member_count}</TableCell>
                      <TableCell>{planBadge(org.subscription_plan, org.subscription_status)}</TableCell>
                      <TableCell>
                        <span className="text-sm">{org.onboarding_progress}</span>
                        {org.onboarding_stuck_at && (
                          <p className="text-xs text-yellow-500">
                            → {STEP_LABELS[org.onboarding_stuck_at] || org.onboarding_stuck_at}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {new Date(org.created_at).toLocaleDateString('es-MX')}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {meta.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2">
            {meta.current_page > 1 && (
              <Button variant="outline" size="sm" asChild>
                <Link href={`/organizations?page=${meta.current_page - 1}${search ? `&search=${search}` : ''}`}>
                  Anterior
                </Link>
              </Button>
            )}
            <span className="text-sm text-muted-foreground">
              Página {meta.current_page} de {meta.total_pages} ({meta.total_count} total)
            </span>
            {meta.current_page < meta.total_pages && (
              <Button variant="outline" size="sm" asChild>
                <Link href={`/organizations?page=${meta.current_page + 1}${search ? `&search=${search}` : ''}`}>
                  Siguiente
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
